import React, {Fragment, useState} from "react";
import {schemaMatches} from "@jsonforms/core";
import {withJsonFormsControlProps} from "@jsonforms/react";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormHelperText,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  useTheme
} from "@mui/material";
import {useTranslation} from "react-i18next";
import CustomJsonForms from "..";
import CustomDialogTitle from "../../custom-dialog-title";

export const FormListTester = schemaMatches(
  schema => (Array.isArray(schema.type) ? schema.type.includes("array") : schema.type === "array") && !schema.renderer
);

export const FormListRenderer = withJsonFormsControlProps(
  ({label, path, handleChange, schema, uischema, data, errors, required, showValidation}) => {
    const {t} = useTranslation();
    const theme = useTheme();

    const [deleteIndex, setDeleteIndex] = useState(null); // Track which item to delete

    const list = data || [];

    const listSchema = schema?.items ?? {};

    const listUischema = uischema?.options?.detail;

    const addElement = () => {
      const newElement =
        schema?.items?.type === "object"
          ? {}
          : schema?.items?.type === "array"
            ? []
            : schema?.items?.type === "string"
              ? ""
              : null;
      handleChange(path, [...list, newElement]);
    };

    const confirmDelete = () => {
      const updatedList = list.filter((_, idx) => idx !== deleteIndex);
      handleChange(path, updatedList);
      setDeleteIndex(null);
    };

    const cancelDelete = () => {
      setDeleteIndex(null);
    };

    const updateElement = (index, updatedData) =>
      handleChange(
        path,
        list.map((item, idx) => (idx === index ? updatedData : item))
      );

    return (
      <>
        <Card
          variant="outlined"
          sx={{
            margin: "8px 0",
            borderColor: errors ? theme.palette.error.main : undefined
          }}
          className="custom-json-forms__renderers__form-list__container"
        >
          <CardContent>
            <Grid container spacing={2}>
              <Grid size={12}>
                <Typography
                  className="custom-json-forms__renderers__form-list__title"
                  sx={{
                    color: errors ? theme.palette.error.main : "inherit"
                  }}
                >
                  {label}
                  {required ? " *" : ""}
                </Typography>
              </Grid>
              {list.map((element, index) => (
                <Fragment key={index}>
                  <Grid
                    size={12}
                    className="custom-json-forms__renderers__form-list__element"
                    sx={{
                      display: "flex",
                      flexWrap: "nowrap",
                      alignItems: "flex-start"
                    }}
                  >
                    <CustomJsonForms
                      schema={listSchema}
                      uischema={listUischema}
                      data={element}
                      setData={data => updateElement(index, data)}
                      errors={errors}
                      showValidation={showValidation}
                    />
                    <Tooltip title={t("jsons_forms.formlist.deleteItem")}>
                      <IconButton
                        onClick={() => setDeleteIndex(index)}
                        size="large"
                        sx={{
                          margin: "12px -8px 0 8px"
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                  <Grid
                    size={12}
                    sx={{
                      paddingTop: "0 !important"
                    }}
                  >
                    <Divider />
                  </Grid>
                </Fragment>
              ))}
              <Grid size={12}>
                <Button
                  startIcon={<AddIcon />}
                  onClick={addElement}
                  disabled={schema.maxItems && list.length >= schema.maxItems}
                >
                  {t("jsons_forms.formlist.addItem")}
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        {errors && (
          <FormHelperText
            error
            sx={{
              marginLeft: "14px",
              marginTop: "-8px"
            }}
          >
            {errors}
          </FormHelperText>
        )}

        <Dialog
          maxWidth="xs"
          open={deleteIndex !== null}
          onClose={cancelDelete}
          className="custom-json-forms__renderers__form-list__delete-dialog"
        >
          <CustomDialogTitle onClose={cancelDelete}>{t("commons.delete.title")}</CustomDialogTitle>
          <DialogContent>{t("commons.delete.content")}</DialogContent>
          <DialogActions>
            <Button autoFocus onClick={cancelDelete}>
              {t("commons.confirm.cancel")}
            </Button>
            <Button onClick={confirmDelete}>{t("commons.confirm.confirm")}</Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);
